import { t } from "@lingui/core/macro"
import { useMutation, useQueryClient, useSuspenseQuery } from "@tanstack/react-query"
import { Link, useNavigate } from "@tanstack/react-router"
import { Archive, Flag, History, Plus, RotateCcw } from "lucide-react"
import { useDeferredValue, useState } from "react"
import { toast } from "sonner"
import { ConfirmDialog } from "@/components/ConfirmDialog"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { useDebouncedSearch } from "@/hooks/useDebouncedSearch"
import { usePermissions } from "@/hooks/usePermissions"
import { flagQueryKeys } from "@/lib/queryKeys"
import { cn } from "@/lib/utils"
import { archiveFlagFn, listFlagsFn } from "../server/flagManagement"

type FlagsListFilters = {
  search?: string
  includeArchived?: boolean
}

type FlagsListProps = {
  filters: FlagsListFilters
  onFiltersChange: (filters: Partial<FlagsListFilters>) => void
}

const flagsListQuery = (filters: FlagsListFilters) => ({
  queryKey: flagQueryKeys.list(filters),
  queryFn: () => listFlagsFn({ data: filters }),
})

export function FlagsList({ filters, onFiltersChange }: FlagsListProps) {
  // Local search state so typing doesn't navigate on every keystroke
  const [searchInput, setSearchInput] = useDebouncedSearch(filters.search ?? "", (search) =>
    onFiltersChange({ search: search || undefined }),
  )

  // Keep showing the previous results while the new search loads, instead of suspending the page
  const deferredSearch = useDeferredValue(filters.search)
  const deferredFilters = { ...filters, search: deferredSearch }

  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { hasPermission } = usePermissions()
  const { data } = useSuspenseQuery(flagsListQuery(deferredFilters))

  const canCreate = hasPermission("featureFlag", "create").allowed
  const canArchive = hasPermission("featureFlag", "archive").allowed
  const canViewAuditLog = hasPermission("auditLog", "read").allowed

  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false)
  const [flagToArchive, setFlagToArchive] = useState<{
    id: string
    key: string
    name: string
  } | null>(null)

  const archiveMutation = useMutation({
    mutationFn: archiveFlagFn,
    onSuccess: () => {
      toast.success(t`Flag archived`)
      setArchiveDialogOpen(false)
      setFlagToArchive(null)
      void queryClient.invalidateQueries({ queryKey: flagQueryKeys.lists() })
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : t`Failed to archive flag`)
    },
  })

  const handleToggleArchived = () => {
    onFiltersChange({ ...filters, includeArchived: !filters.includeArchived })
  }

  const handleArchive = (flag: { id: string; key: string; name: string }) => {
    setFlagToArchive(flag)
    setArchiveDialogOpen(true)
  }

  const handleConfirmArchive = () => {
    if (!flagToArchive) return
    archiveMutation.mutate({ data: { flagId: flagToArchive.id } })
  }

  const formatDate = (date: Date | string) => {
    return new Date(date).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    })
  }

  return (
    <>
      <div className="flex-1 space-y-6 overflow-y-auto pb-8">
        <div className="flex items-start justify-between gap-6">
          <div>
            <div className="mb-2 text-xs uppercase tracking-wider text-neutral-500 dark:text-neutral-300">
              {t`Configuration`}
            </div>
            <h1 className="mb-2 font-serif text-4xl font-bold text-neutral-900 dark:text-white">
              {t`Feature Flags`}
            </h1>
            <p className="text-neutral-500 dark:text-neutral-300">
              {t`Manage feature flags for controlled rollouts and targeting`}
            </p>
          </div>
          {canCreate && (
            <Button onClick={() => void navigate({ to: "/settings/feature-flags/new" as const })}>
              <Plus className="mr-2 size-4" />
              {t`New Flag`}
            </Button>
          )}
        </div>

        <div className="space-y-4">
          <div className="flex items-center gap-4">
            <Input
              placeholder={t`Search flags...`}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="max-w-sm"
            />
            <Button
              variant={filters.includeArchived ? "secondary" : "ghost"}
              size="sm"
              onClick={handleToggleArchived}
            >
              <Archive className="mr-2 size-4" />
              {filters.includeArchived ? t`Hide Archived` : t`Show Archived`}
            </Button>
          </div>

          <div className="border-neutral-150 overflow-hidden rounded-lg border bg-white shadow-sm dark:border-neutral-700 dark:bg-neutral-900/50">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="px-4 text-left font-medium">{t`Key`}</TableHead>
                    <TableHead className="px-4 text-left font-medium">{t`Name`}</TableHead>
                    <TableHead className="px-4 text-left font-medium">{t`Type`}</TableHead>
                    <TableHead className="px-4 text-left font-medium">{t`Status`}</TableHead>
                    <TableHead className="px-4 text-left font-medium">{t`Created`}</TableHead>
                    <TableHead className="px-4 text-right font-medium">{t`Actions`}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.flags.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="h-24 px-4 text-center">
                        <Flag className="mx-auto mb-2 h-5 w-5 text-gray-400" />
                        <p>{t`No feature flags found.`}</p>
                        {canCreate && (
                          <p className="mt-1 text-sm text-neutral-400">
                            {t`Create your first flag to get started.`}
                          </p>
                        )}
                      </TableCell>
                    </TableRow>
                  ) : (
                    data.flags.map((flag) => {
                      const isArchived = !!flag.archivedAt
                      return (
                        <TableRow
                          key={flag.id}
                          className={cn(
                            "cursor-pointer transition-colors hover:bg-neutral-50 dark:hover:bg-neutral-800/50",
                            isArchived && "opacity-60",
                          )}
                          onClick={() => {
                            void navigate({
                              to: "/settings/feature-flags/$flagId",
                              params: { flagId: flag.id },
                            })
                          }}
                        >
                          <TableCell className="px-4 font-mono text-sm">
                            <div className="flex items-center gap-2">
                              <Flag className="size-4 text-neutral-400" />
                              {flag.key}
                            </div>
                          </TableCell>
                          <TableCell className="px-4">{flag.name}</TableCell>
                          <TableCell className="px-4">
                            <Badge variant="outline">{flag.valueType}</Badge>
                          </TableCell>
                          <TableCell className="px-4">
                            {(() => {
                              const prodEnv = flag.flagEnvironments.find(
                                (e) => e.environment === "production",
                              )
                              return (
                                <div className="flex gap-2">
                                  {prodEnv?.enabled ? (
                                    <Badge variant="success">Enabled</Badge>
                                  ) : (
                                    <Badge variant="outline">Disabled</Badge>
                                  )}
                                  {isArchived && <Badge variant="secondary">Archived</Badge>}
                                </div>
                              )
                            })()}
                          </TableCell>
                          <TableCell className="px-4 text-neutral-500">
                            {formatDate(flag.createdAt)}
                          </TableCell>
                          <TableCell className="px-4 text-right">
                            {/* oxlint-disable-next-line jsx-a11y/click-events-have-key-events -- Event propagation stopper */}
                            <div
                              className="flex items-center justify-end gap-2"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {canArchive && !isArchived && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleArchive(flag)}
                                  title={t`Archive`}
                                >
                                  <Archive className="size-4" />
                                </Button>
                              )}
                              {isArchived && (
                                <Badge variant="secondary">
                                  <RotateCcw className="mr-1 size-3" />
                                  {t`Archived`}
                                </Badge>
                              )}
                              {canViewAuditLog && (
                                <Link
                                  to="/settings/audit"
                                  search={{
                                    entityType: "featureFlag",
                                    entityId: flag.id,
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <Button variant="ghost" size="sm" title={t`View Audit Log`}>
                                    <History className="size-4" />
                                  </Button>
                                </Link>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      )
                    })
                  )}
                </TableBody>
              </Table>
            </div>
          </div>

          {data.total > data.flags.length && (
            <div className="text-center text-sm text-neutral-500">
              {t`Showing ${data.flags.length} of ${data.total} flags`}
            </div>
          )}
        </div>
      </div>

      <ConfirmDialog
        title={t`Archive Flag`}
        description={
          flagToArchive
            ? t`Are you sure you want to archive "${flagToArchive.name}" (${flagToArchive.key})? The flag will no longer be evaluated and will return default values.`
            : t`Are you sure you want to archive this flag?`
        }
        confirmLabel={t`Archive`}
        cancelLabel={t`Cancel`}
        open={archiveDialogOpen}
        onOpenChange={(open) => {
          if (!archiveMutation.isPending) setArchiveDialogOpen(open)
        }}
        onConfirm={handleConfirmArchive}
        onCancel={() => {
          setArchiveDialogOpen(false)
          setFlagToArchive(null)
        }}
        loading={archiveMutation.isPending}
      />
    </>
  )
}

export { flagsListQuery }
